import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UserProgress } from '../services/courseService';
import { mockCourseService, MOCK_COURSES } from '../services/mockData';

export const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  const [userProgress, setUserProgress] = useState<UserProgress[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUserProgress = async () => {
      try {
        const progress = await mockCourseService.getUserEnrollments();
        setUserProgress(progress);
      } catch (error) {
        console.error('Failed to load user progress:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserProgress();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Calculate stats
  const totalEnrollments = userProgress.length;
  const completedCourses = userProgress.filter(p => p.is_completed).length;
  const totalPointsEarned = userProgress.reduce((sum, p) => sum + p.total_points_earned, 0);
  const totalTimeSpent = Math.round(userProgress.reduce((sum, p) => sum + p.total_time_spent_minutes, 0) / 60); // Convert to hours
  const averageProgress = Math.round(userProgress.reduce((sum, p) => sum + p.completion_percentage, 0) / userProgress.length) || 0;

  // Get courses with progress
  const coursesWithProgress = userProgress.map(progress => ({
    ...progress,
    course: MOCK_COURSES.find(c => c.id === progress.course_id)
  }));

  // Calculate skill level based on points and completions
  const getSkillLevel = () => {
    if (totalPointsEarned >= 200 && completedCourses >= 2) return 'Advanced';
    if (totalPointsEarned >= 100 && completedCourses >= 1) return 'Intermediate';
    return 'Beginner';
  };

  const getSkillLevelColor = (level: string) => {
    switch (level) {
      case 'Advanced': return 'text-purple-600 bg-purple-100';
      case 'Intermediate': return 'text-orange-600 bg-orange-100';
      default: return 'text-green-600 bg-green-100';
    }
  };

  // Recent achievements
  const achievements = [
    { title: 'First Steps', description: 'Completed your first lesson', icon: '🎯', earned: totalPointsEarned > 0 },
    { title: 'Knowledge Seeker', description: 'Enrolled in your first course', icon: '📚', earned: totalEnrollments > 0 },
    { title: 'Fast Learner', description: 'Completed a course in under a week', icon: '⚡', earned: completedCourses > 0 },
    { title: 'Dedicated Student', description: 'Spent 10+ hours learning', icon: '🏆', earned: totalTimeSpent >= 10 },
    { title: 'Point Collector', description: 'Earned 100+ points', icon: '⭐', earned: totalPointsEarned >= 100 },
    { title: 'Course Crusher', description: 'Completed 2+ courses', icon: '🎓', earned: completedCourses >= 2 },
  ];

  const earnedAchievements = achievements.filter(a => a.earned);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Your Profile</h1>
        <p className="text-gray-600 mt-2">Track your learning progress and achievements</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Profile Info & Stats */}
        <div className="lg:col-span-1 space-y-6">
          {/* Profile Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="text-center">
              <div className="w-20 h-20 bg-indigo-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-2xl font-bold text-indigo-600">
                  {user?.username?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <h2 className="text-xl font-bold text-gray-900">{user?.username || 'Student'}</h2>
              <p className="text-gray-600">{user?.email}</p>
              <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mt-3 ${getSkillLevelColor(getSkillLevel())}`}>
                {getSkillLevel()} Level
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Learning Stats</h3>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-2 gap-4">
              <div className="text-center p-3 bg-indigo-50 rounded-lg">
                <div className="text-2xl font-bold text-indigo-600">{totalEnrollments}</div>
                <div className="text-sm text-gray-600">Courses Enrolled</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{completedCourses}</div>
                <div className="text-sm text-gray-600">Completed</div>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{totalPointsEarned}</div>
                <div className="text-sm text-gray-600">Total Points</div>
              </div>
              <div className="text-center p-3 bg-orange-50 rounded-lg">
                <div className="text-2xl font-bold text-orange-600">{totalTimeSpent}h</div>
                <div className="text-sm text-gray-600">Time Spent</div>
              </div>
            </div>
          </div>

          {/* Achievements */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Achievements</h3>
            <div className="space-y-3">
              {earnedAchievements.map((achievement, index) => (
                <div key={index} className="flex items-center p-3 bg-yellow-50 rounded-lg">
                  <span className="text-2xl mr-3">{achievement.icon}</span>
                  <div>
                    <h4 className="font-medium text-gray-900">{achievement.title}</h4>
                    <p className="text-sm text-gray-600">{achievement.description}</p>
                  </div>
                </div>
              ))}
              {earnedAchievements.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <span className="text-4xl mb-2 block">🏆</span>
                  <p>Complete your first lesson to earn achievements!</p>
                </div>
              )}
            </div>
          </div>

          {/* Learning Streak */}
          <div className="bg-gradient-to-r from-orange-400 to-red-500 rounded-xl shadow-sm p-6 text-white">
            <h3 className="text-lg font-bold mb-2">🔥 Learning Streak</h3>
            <div className="text-3xl font-bold mb-2">7 days</div>
            <p className="text-orange-100 text-sm">
              Great job! Keep learning to maintain your streak.
            </p>
          </div>
        </div>

        {/* Right Column - Course Progress */}
        <div className="lg:col-span-2 space-y-6">
          {/* Overall Progress */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Overall Progress</h3>
              <span className="text-indigo-600 font-medium">{averageProgress}% Complete</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="bg-indigo-600 h-3 rounded-full transition-all duration-300"
                style={{ width: `${averageProgress}%` }}
              ></div>
            </div>
            <p className="text-gray-600 text-sm mt-2">
              {totalEnrollments > 0 
                ? `You're making great progress across ${totalEnrollments} course${totalEnrollments > 1 ? 's' : ''}!`
                : 'Start your first course to see your progress here.'
              }
            </p>
          </div>

          {/* Course Progress */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-6">Your Courses</h3>
            {coursesWithProgress.length > 0 ? (
              <div className="space-y-6">
                {coursesWithProgress.map((item) => {
                  const course = item.course;
                  if (!course) return null;

                  return (
                    <div key={item.id} className="border border-gray-200 rounded-lg p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <Link 
                            to={`/courses/${course.id}`}
                            className="text-xl font-bold text-gray-900 hover:text-indigo-600 transition-colors"
                          >
                            {course.title}
                          </Link>
                          <p className="text-gray-600 mt-1">{course.short_description}</p>
                          <div className="flex items-center space-x-4 mt-3 text-sm text-gray-500">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              course.difficulty_level === 'beginner' ? 'bg-green-100 text-green-800' :
                              course.difficulty_level === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {course.difficulty_level}
                            </span>
                            <span>{course.estimated_duration_hours} hours</span>
                            <span>⭐ {course.average_rating}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-indigo-600">
                            {item.completion_percentage}%
                          </div>
                          <div className="text-sm text-gray-500">Complete</div>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="mb-4">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${item.completion_percentage}%` }}
                          ></div>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center text-sm">
                        <div className="bg-gray-50 p-2 rounded">
                          <div className="font-medium text-gray-900">
                            {item.completed_lessons}/{item.total_lessons}
                          </div>
                          <div className="text-gray-600 text-xs sm:text-sm">Lessons</div>
                        </div>
                        <div className="bg-gray-50 p-2 rounded">
                          <div className="font-medium text-gray-900">
                            {item.total_points_earned}
                          </div>
                          <div className="text-gray-600 text-xs sm:text-sm">Points</div>
                        </div>
                        <div className="bg-gray-50 p-2 rounded">
                          <div className="font-medium text-gray-900">
                            {Math.round(item.total_time_spent_minutes / 60)}h
                          </div>
                          <div className="text-gray-600 text-xs sm:text-sm">Time</div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        {item.is_completed ? (
                          <div className="flex items-center justify-between">
                            <span className="text-green-600 font-medium flex items-center">
                              ✅ Course Completed!
                            </span>
                            <Link 
                              to={`/courses/${course.id}`}
                              className="text-indigo-600 hover:text-indigo-500 font-medium"
                            >
                              Review Course
                            </Link>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600 text-sm">
                              Last accessed: {new Date(item.last_accessed_at || item.enrolled_at).toLocaleDateString()}
                            </span>
                            <Link 
                              to={item.current_lesson_id ? `/courses/${course.id}/lessons/${item.current_lesson_id}` : `/courses/${course.id}`}
                              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
                            >
                              Continue Learning
                            </Link>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <span className="text-6xl mb-4 block">📚</span>
                <h4 className="text-xl font-bold text-gray-900 mb-2">No Courses Yet</h4>
                <p className="text-gray-600 mb-6">
                  Start your machine learning journey by enrolling in your first course!
                </p>
                <Link 
                  to="/courses"
                  className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors inline-block"
                >
                  Browse Courses
                </Link>
              </div>
            )}
          </div>

          {/* Learning Goals */}
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl shadow-sm p-6 text-white">
            <h3 className="text-lg font-bold mb-4">🎯 Learning Goals</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span>Complete 2 courses this month</span>
                <span className="text-indigo-200">{completedCourses}/2</span>
              </div>
              <div className="w-full bg-indigo-400 rounded-full h-2">
                <div 
                  className="bg-white h-2 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min((completedCourses / 2) * 100, 100)}%` }}
                ></div>
              </div>
              <div className="flex items-center justify-between text-sm text-indigo-200">
                <span>Earn 200 points</span>
                <span>{totalPointsEarned}/200</span>
              </div>
              <div className="w-full bg-indigo-400 rounded-full h-2">
                <div 
                  className="bg-white h-2 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min((totalPointsEarned / 200) * 100, 100)}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};